import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getSvgRegistryContext } from './SvgRegistryContext';

let id = -1;

export function useSvgOptimizer(
  optimizationId: string,
  enabled: boolean
): { keep: boolean; pack: boolean; reuse: boolean } {
  const [, updateState] = useState<Record<never, never>>();
  const SvgRegistryContext = getSvgRegistryContext();
  const svgRegistry = useContext(SvgRegistryContext);
  const cleanUnregisterListenerRef = useRef<() => void>();
  const unregisterSvgRef = useRef<() => void>();
  const mountedRef = useRef(false);
  const forceUpdateRef = useRef(false);
  const instanceId = React.useId
    ? React.useId()
    : useMemo(() => {
        id += 1;
        return `${id}`;
      }, []);

  (typeof window !== 'undefined' ? useLayoutEffect : useEffect)(() => {
    if (!enabled) {
      return;
    }
    mountedRef.current = true;

    if (forceUpdateRef.current) {
      forceUpdateRef.current = false;
      updateState({});
    }
    return () => {
      mountedRef.current = false;

      if (unregisterSvgRef.current) {
        unregisterSvgRef.current();
        unregisterSvgRef.current = undefined;
      }
      if (cleanUnregisterListenerRef.current) {
        cleanUnregisterListenerRef.current();
        cleanUnregisterListenerRef.current = undefined;
      }
    };
  }, [enabled]);

  if (enabled && svgRegistry !== undefined) {
    const { hasSvg, registerSvg } = svgRegistry;

    if (cleanUnregisterListenerRef.current === undefined) {
      const forceUpdate = () => {
        if (mountedRef.current) {
          updateState({});
        } else {
          forceUpdateRef.current = true;
        }
      };
      cleanUnregisterListenerRef.current = svgRegistry.addUnregisterListener(
        optimizationId,
        forceUpdate
      );
    }

    if (unregisterSvgRef.current === undefined) {
      if (hasSvg(optimizationId, instanceId)) {
        return { keep: false, pack: false, reuse: true };
      }
      unregisterSvgRef.current = registerSvg(optimizationId, instanceId);
    }
  }
  return {
    keep: !enabled || !svgRegistry,
    pack: enabled && !!svgRegistry,
    reuse: false,
  };
}
